import { ApiError } from '@gorila-shared-ui/components';
import { PaginatedResponse, PdfSettings } from '../types/app';
import { JobType, JobActivity, JobActivityCore, JobTypeActivityRequest, JobTypeRequest } from '../types/jobs';
import { getRequest, postRequest } from './api';
import { URLS } from './urls/urls';

export const getJobTypes = async (
  page: number = 1,
  search: string = '',
  projection: string = 'full'
): Promise<{
  paginatedResponse?: PaginatedResponse<JobType>;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.types.all, {
    queryParams: { page, search, projection },
  });
  return {
    paginatedResponse: response?.data,
    error: response?.error,
  };
};

export const getJobType = async (
  jobTypeId: string
): Promise<{
  jobType?: JobType;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.types.detail(jobTypeId));
  return {
    jobType: response?.data,
    error: response?.error,
  };
};

export const createJobType = async (jobType: JobTypeRequest): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.types.create, {
    body: {
      ...jobType,
      technicianSkills: jobType.technicianSkills.map((skill) => skill.slug),
    },
  });
  return {
    error: response?.error,
  };
};

export const updateJobType = async (jobTypeId: string, jobType: JobTypeRequest): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.types.update(jobTypeId), {
    body: {
      ...jobType,
      technicianSkills: jobType.technicianSkills.map((skill) => skill.slug),
    },
  });
  return {
    error: response?.error,
  };
};

export const getJobActivities = async (
  jobTypeId: string,
  page: number = 1,
  search: string = '',
  projection: string = 'full'
): Promise<{
  paginatedResponse?: PaginatedResponse<JobActivityCore>;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.types.activities.all(jobTypeId), {
    queryParams: { page, search, projection },
  });
  return {
    paginatedResponse: response?.data,
    error: response?.error,
  };
};

export const getJobActivity = async (
  jobTypeId: string,
  activityId: string
): Promise<{
  activity?: JobActivity;
  error?: ApiError;
}> => {
  const response = await getRequest(URLS.jobs.types.activities.detail(jobTypeId, activityId));
  return {
    activity: response?.data,
    error: response?.error,
  };
};

export const createJobActivity = async (
  jobTypeId: string,
  activity: JobTypeActivityRequest
): Promise<{ id?: string; error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.types.activities.create(jobTypeId), {
    body: {
      ...activity,
    },
  });
  return {
    id: response?.data?._id,
    error: response?.error,
  };
};

export const updateJobActivity = async (
  jobTypeId: string,
  activityId: string,
  activity: JobTypeActivityRequest
): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.types.activities.update(jobTypeId, activityId), {
    body: {
      ...activity,
    },
  });
  return {
    error: response?.error,
  };
};

export const updatePdfSettings = async (jobTypeId: string, pdfSettings: PdfSettings): Promise<{ error?: ApiError }> => {
  const response = await postRequest(URLS.jobs.types.pdfSettings.update(jobTypeId), {
    body: {
      ...pdfSettings,
    },
  });
  return {
    error: response?.error,
  };
};
